import { useEffect, useState } from 'react';
import {
  getPresetDateRange,
  matchDateRangePreset,
  type DateRangePreset,
  type PartnerFilterState,
  type PlatformFilter,
} from '../../lib/partnerStatsFilters';
import styles from './DashboardFilters.module.css';

const PLATFORM_OPTIONS: { value: PlatformFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'desktop', label: 'Desktop' },
];

const PRESET_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
];

interface DashboardFiltersProps {
  filters: PartnerFilterState;
  onChange: (next: PartnerFilterState) => void;
}

export function DashboardFilters({ filters, onChange }: DashboardFiltersProps) {
  const [draftDateFrom, setDraftDateFrom] = useState(filters.dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(filters.dateTo);
  const [customMode, setCustomMode] = useState(
    () => matchDateRangePreset(filters.dateFrom, filters.dateTo) === null,
  );

  useEffect(() => {
    setDraftDateFrom(filters.dateFrom);
    setDraftDateTo(filters.dateTo);
  }, [filters.dateFrom, filters.dateTo]);

  function setPlatform(platform: PlatformFilter) {
    onChange({ ...filters, platform });
  }

  function selectPreset(preset: DateRangePreset) {
    setCustomMode(false);
    const range = getPresetDateRange(preset);
    onChange({ ...filters, ...range });
  }

  function applyDates() {
    onChange({ ...filters, dateFrom: draftDateFrom, dateTo: draftDateTo });
  }

  const matchedPreset = customMode
    ? null
    : matchDateRangePreset(filters.dateFrom, filters.dateTo);
  const hasPendingDateChanges =
    draftDateFrom !== filters.dateFrom || draftDateTo !== filters.dateTo;

  return (
    <div className={styles.filtersCard}>
      <div className={styles.filtersBody}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Platform</span>
          <div className={styles.pillGroup} role="group" aria-label="Platform filter">
            {PLATFORM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={
                  filters.platform === opt.value ? styles.pillActive : styles.pill
                }
                onClick={() => setPlatform(opt.value)}
                aria-pressed={filters.platform === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Date range</span>
          <div className={styles.pillGroup} role="group" aria-label="Date range filter">
            {PRESET_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={matchedPreset === opt.value ? styles.pillActive : styles.pill}
                onClick={() => selectPreset(opt.value)}
                aria-pressed={matchedPreset === opt.value}
              >
                {opt.label}
              </button>
            ))}
            <button
              type="button"
              className={matchedPreset === null ? styles.pillActive : styles.pill}
              onClick={() => setCustomMode(true)}
              aria-pressed={matchedPreset === null}
            >
              Custom
            </button>
          </div>
        </div>
      </div>
      {customMode ? (
        <div className={styles.dateRow}>
          <input
            type="date"
            className={styles.dateInput}
            value={draftDateFrom}
            onChange={(e) => setDraftDateFrom(e.target.value)}
            max={draftDateTo || undefined}
            aria-label="From date"
          />
          <span className={styles.dateSeparator}>to</span>
          <input
            type="date"
            className={styles.dateInput}
            value={draftDateTo}
            onChange={(e) => setDraftDateTo(e.target.value)}
            min={draftDateFrom || undefined}
            aria-label="To date"
          />
          <button
            type="button"
            className={styles.applyBtn}
            onClick={applyDates}
            disabled={!hasPendingDateChanges}
          >
            Apply
          </button>
        </div>
      ) : null}
    </div>
  );
}
